import { useEffect, useRef } from 'react';

import { useDecisions } from '@libs/common-next';

import { useProduct } from '@/entities/product';
import {
    clearErrors,
    reset,
    usePurchaseFormState,
    usePurchaseFormValue,
    usePurchaseFormWatch,
} from '@/entities/purchase-form';
import { ROOT_CART_SUBMIT_ERROR_KEY } from '@/widgets/product-details/purchase-panel/model/useHasOnlyCartSubmitError';

import { getCtaText } from '../lib';

const RESET_TIME = 3000;

export const useAddedToCartCTA = () => {
    const autoshipFlag = useDecisions('ff_autoship');

    const { product } = useProduct();

    const { isSubmitting, isSubmitted, isSubmitSuccessful, errors } = usePurchaseFormState();
    const deliveryMethod = usePurchaseFormWatch('fulfilmentMethod');
    const purchaseOption = usePurchaseFormWatch('purchaseOption');
    const subscriptionType = usePurchaseFormWatch('subscriptionType');
    const autoshipFrequency = usePurchaseFormWatch('autoshipFrequency');

    // Share purchase option and fulfillment method to the atc button for client-side metric in Optimizely

    const purchaseAbbreviation = purchaseOption == 'autoship' ? 'AS' : 'OT';
    // Current Form Watch doesn't track default value once user gets to the page
    const getValues = usePurchaseFormValue();
    const { fulfilmentMethod } = getValues();
    const metaPurchaseDelivery = `${purchaseAbbreviation}_${fulfilmentMethod?.toUpperCase()}`;

    // Track previous values to detect changes
    const prevValuesRef = useRef({
        sku: product?.sku,
        deliveryMethod,
        purchaseOption,
        autoshipFrequency,
    });

    // Reset ATC button to its original state when SKU, delivery method, purchase option, or autoship frequency changes
    useEffect(() => {
        const prev = prevValuesRef.current;
        const valuesChanged =
            prev.sku !== product?.sku ||
            prev.deliveryMethod !== deliveryMethod ||
            prev.purchaseOption !== purchaseOption ||
            prev.autoshipFrequency !== autoshipFrequency;

        const allPrevWereEmpty = !prev.sku && !prev.deliveryMethod && !prev.purchaseOption && !prev.autoshipFrequency;

        if (isSubmitted && valuesChanged && !allPrevWereEmpty) {
            clearErrors(ROOT_CART_SUBMIT_ERROR_KEY);
            reset(undefined, { keepIsSubmitted: false, keepValues: true, keepErrors: true });
        }

        prevValuesRef.current = {
            sku: product?.sku,
            deliveryMethod,
            purchaseOption,
            autoshipFrequency,
        };
    }, [product?.sku, deliveryMethod, purchaseOption, autoshipFrequency, reset, isSubmitted, clearErrors]);

    // Auto-reset ATC button to its original state after 3 seconds
    useEffect(() => {
        if (isSubmitted) {
            const timeoutId = setTimeout(() => {
                clearErrors(ROOT_CART_SUBMIT_ERROR_KEY);
                reset(undefined, { keepIsSubmitted: false, keepValues: true, keepErrors: true });
            }, RESET_TIME);
            return () => clearTimeout(timeoutId);
        }
    }, [isSubmitted, reset, clearErrors]);

    const isSubmittedSuccessfully = isSubmitSuccessful && !Object.keys(errors).length;
    const isAutoshipSelected = purchaseOption === 'autoship';
    const isNotPharmacy = !product?.pharmacy;

    const shouldShowAutoshipText =
        autoshipFlag === 'crawl' && subscriptionType === 'existing' && isAutoshipSelected && isNotPharmacy;

    const { add: addText, added: addedText } = getCtaText({ isAutoshipText: shouldShowAutoshipText });
    const ctaText = isSubmittedSuccessfully ? addedText : addText;

    return { isSubmitting, isSubmittedSuccessfully, ctaText, metaPurchaseDelivery };
};
